import {
  Color3,
  DynamicTexture,
  Mesh,
  MeshBuilder,
  Texture,
  Vector2,
  type AbstractMesh,
  type Scene,
} from "@babylonjs/core";
import { WaterMaterial } from "@babylonjs/materials";

/**
 * Reflective/refractive ocean plane. Art direction (see docs/Castaway
 * Plan.md): calm, glassy, pale-turquoise water — low wave amplitude,
 * gentle reflections, no whitecap/foam turbulence — rather than a stormy
 * or highly dynamic sea.
 */
export class OceanWater {
  readonly mesh: Mesh;
  readonly material: WaterMaterial;

  constructor(scene: Scene, size = 4000) {
    this.mesh = MeshBuilder.CreateGround("ocean", { width: size, height: size, subdivisions: 32 }, scene);
    this.mesh.position.y = -0.05; // slightly below the island's sand level

    this.material = new WaterMaterial("oceanMaterial", scene, new Vector2(512, 512));
    this.material.bumpTexture = this.createProceduralBumpTexture(scene);
    this.material.backFaceCulling = true;
    this.material.windForce = -4; // gentle — calm water per art direction
    this.material.waveHeight = 0.05;
    this.material.bumpHeight = 0.15;
    this.material.waveLength = 14;
    this.material.waveSpeed = 6;
    this.material.colorBlendFactor = 0.35;
    this.material.waterColor = new Color3(0.32, 0.68, 0.66); // pale turquoise, per reference
    this.mesh.material = this.material;

    // The mesh's own transform never moves (only the material's shader
    // animates the surface), so its world matrix is safe to freeze —
    // Phase 4 perf pass. Doesn't affect the reflection/refraction render
    // targets, which recompute every frame regardless.
    this.mesh.freezeWorldMatrix();
  }

  /** Registers a mesh to appear in the water's reflection/refraction. */
  addToRenderList(mesh: AbstractMesh): void {
    this.material.addToRenderList(mesh);
  }

  /**
   * WaterMaterial needs a bump map for wave-normal perturbation. Generated
   * locally (rather than fetched from a remote texture URL) to keep the app
   * free of a runtime network dependency for a core render asset.
   */
  private createProceduralBumpTexture(scene: Scene): DynamicTexture {
    const size = 256;
    const texture = new DynamicTexture("oceanBump", size, scene, false);
    const ctx = texture.getContext() as CanvasRenderingContext2D;
    const imageData = ctx.createImageData(size, size);
    for (let i = 0; i < imageData.data.length; i += 4) {
      // Blue-dominant "neutral-ish normal map" noise with subtle R/G jitter
      // — gentle wave-normal variation, not a dramatically choppy surface.
      imageData.data[i] = 110 + Math.random() * 25;
      imageData.data[i + 1] = 110 + Math.random() * 25;
      imageData.data[i + 2] = 200 + Math.random() * 30;
      imageData.data[i + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    texture.update();
    texture.wrapU = Texture.WRAP_ADDRESSMODE;
    texture.wrapV = Texture.WRAP_ADDRESSMODE;
    return texture;
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
  }
}
